import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { apiUrl } from "../../utils/apiUrl";
import "./leaderboard.css";

const FLAWLESS_MEDAL = "/img/complete_all_stages_medal.png";
const MEDAL_BY_BADGE_LEVEL = {
  10: "/img/level10_medal.png",
  7: "/img/level7_medal.png",
  5: "/img/level5_medal.png",
  3: "/img/level3_medal.png",
};
const MEDAL_BADGE_LEVELS = [10, 7, 5, 3];

function getMedalImage(levelValue, isFlawless = false) {
  const completedLevel = Number(levelValue) || 0;
  if (completedLevel >= 10 && isFlawless) {
    return FLAWLESS_MEDAL;
  }
  for (const badgeLevel of MEDAL_BADGE_LEVELS) {
    if (completedLevel >= badgeLevel) {
      return MEDAL_BY_BADGE_LEVEL[badgeLevel];
    }
  }
  return "";
}

function getMedalTitle(levelValue, isFlawless = false) {
  const completedLevel = Number(levelValue) || 0;
  if (completedLevel >= 10 && isFlawless) return "Flawless Run";
  if (completedLevel >= 10) return "Recall Expert";
  if (completedLevel >= 7) return "Memory Pro";
  if (completedLevel >= 5) return "Pattern Player";
  if (completedLevel >= 3) return "Beginner Mind";
  return "Medal";
}

function openMedalPopup(player) {
  const bestLevel = player?.bestLevel || player?.level;
  const medal = getMedalImage(bestLevel, player?.isFlawless);
  if (!medal) return;
  Swal.fire({
    title: `${player?.name || "Player"} earned badge`,
    imageUrl: medal,
    imageAlt: getMedalTitle(bestLevel, player?.isFlawless),
    imageWidth: 220,
    confirmButtonText: "OK",
    customClass: {
      popup: "leaderboard-badge-popup",
      title: "leaderboard-badge-popup__title",
      image: "leaderboard-badge-popup__image",
      confirmButton: "leaderboard-badge-popup__ok",
    },
    buttonsStyling: false,
  });
}

export default function Leaderboard() {
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (!user?.sessionId || !user?.organizationId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/leaderboard"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: user.sessionId,
            organizationId: user.organizationId,
            userId: user.userId,
            limit: 5,
          }),
        });
        const data = await resp.json();
        if (!cancelled && data.success) setPlayers(data.players || []);
      } catch {
        // Ignore transient errors; empty state is shown.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.sessionId, user?.organizationId]);

  return (
    <main
      className="leaderboard-page"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL || ""}${isMobile ? "/img/mobile.jpg" : "/img/desktop.jpg"})`,
      }}
    >
      <section className="leaderboard-card">
        <h1>LEADERBOARD</h1>
        {loading ? <p className="leaderboard-state-text">Loading...</p> : null}
        {!loading && players.length === 0 ? <p className="leaderboard-state-text">No records yet.</p> : null}
        {!loading && players.length > 0 ? (
          <div className="leaderboard-list">
            {players.map((p) => (
              <div
                key={`${p.userId}_${p.rank}`}
                className={`leaderboard-row${p.isCurrentUser ? " leaderboard-row--current" : ""}`}
              >
                <span className="leaderboard-rank">{p.rank}</span>
                <div className="leaderboard-pill">
                  <span className="leaderboard-name-cell">
                    {getMedalImage(p.bestLevel || p.level, p.isFlawless) ? (
                      <button
                        type="button"
                        className="leaderboard-medal-btn"
                        onClick={() => openMedalPopup(p)}
                        aria-label={`View ${p.name} medal`}
                      >
                        <img
                          src={getMedalImage(p.bestLevel || p.level, p.isFlawless)}
                          alt={getMedalTitle(p.bestLevel || p.level, p.isFlawless)}
                          className="leaderboard-medal"
                        />
                      </button>
                    ) : (
                      <span className="leaderboard-medal-slot" aria-hidden="true" />
                    )}
                    <span className="leaderboard-name-text">{p.name}</span>
                  </span>
                  <span className="leaderboard-time">{p.bestTime || p.time}</span>
                  <span className="leaderboard-level">{p.bestLevel || p.level}</span>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <button
          type="button"
          className="leaderboard-play-again"
          onClick={() => navigate("/game", { replace: true })}
        >
          Play Again
        </button>
      </section>
    </main>
  );
}
